import React, {useCallback, useContext, useEffect, useState} from 'react';
import * as yup from 'yup';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  InputProps,
  Radio,
  TextField,
  Typography,
  Grid,
  RadioGroup,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import {EnumType} from 'typescript';
import clsx from 'clsx';
import moment, {Moment} from 'moment';
import {get, isArray} from 'lodash-es';
import AutoSelect from './AutoSelectNew';
import AutoDateTimePicker from './AutoDateTimeField';
import AutoTextNew from './AutoTextNew';
import {FormReadOnlyContext} from './AutoFormNew';
import AutoSelectAutocomplete from './AutoSelectAutocomplete';
import {GetObjectLeaves} from '../../utils/flattenTypescriptObject';
import {AutoBoolean} from './AutoBoolean';
import {useAutoForm, useAutoField} from './useAutoForm';
import {getCurrencyString} from '../../utils/currencyFormatter';
import {AutoFieldDebug} from './AutoFieldDebug';
import {SaltusAny} from '../../utils/SaltusAny';
import {UPDATES} from '../../shared/UPDATES';

export enum ActionType {
  buttonGroup = 'buttonGroup',
  checkboxToggle = 'checkboxToggle',
  checkboxGroup = 'checkboxGroup',
  select = 'select',
  selectAutocomplete = 'selectAutocomplete',
  selectGroup = 'selectGroup',
  datepicker = 'datepicker',
  // keyboarddatepicker = 'keyboarddatepicker',
  timepicker = 'timepicker',
  datetimepicker = 'datetimepicker',
  component = 'component',
  radioGroup = 'radioGroup',
  text = 'text',
  boolean = 'boolean',
  enum = 'enum',
  object = 'object',
}

export type FormFieldBase<FormValues = null> = {
  id: FormValues extends object ? GetObjectLeaves<FormValues, 3> : string;
  type: ActionType;
  label: string;
  labelSize?: 'small' | 'medium' | 'large';
  size?: 'medium' | 'small' | undefined;
  initialValue?: FieldValue;
  canBeEmpty?: boolean;
  requireSubmit?: boolean;
  validation?: yup.SchemaOf<any> | ((val: SaltusAny) => string | undefined);
  disabled?: boolean;
  readOnly?: boolean;
  render?: (value: FieldValue) => string;
  needsValue?: boolean;
  helper?: string;
  loading?: boolean;
  fullWidth?: boolean;
  boldLabel?: boolean;
  private?: boolean;
};

export type OptionValueType = string | number;

export type Option<T = OptionValueType> = {
  label: string;
  value: T;
  disabled?: boolean;
  capitalise?: boolean;
};

export type SelectOption<X = OptionValueType> = {
  value: X;
  label: string;
  disabled?: boolean;
  group?: string;
};

export type ButtonOption<T = OptionValueType> = {
  label: string;
  value: T;
  size?: 'small' | 'large' | 'medium';
  disabled?: boolean;
};

export type RadioGroupField<
  FormValues = null,
  T = FieldValue
> = FormFieldBase<FormValues> & {
  type: ActionType.radioGroup;
  options: Option<T>[];
  horizontal?: boolean;
  horizontalOptionWidthXs?: number;
  horizontalOptionWidthMd?: number;
  horizontalOptionWidthLg?: number;
};

export type CheckboxToggleField<
  FormValues = null,
  T = FieldValue
> = FormFieldBase<FormValues> & {
  type: ActionType.checkboxToggle;
  // options: Option<T>[];
};

export type CheckboxGroupField<
  FormValues = null,
  T = FieldValue
> = FormFieldBase<FormValues> & {
  type: ActionType.checkboxGroup;
  options: Option<T>[];
  horizontal?: boolean;
  horizontalOptionWidthXs?: number;
  horizontalOptionWidthMd?: number;
  horizontalOptionWidthLg?: number;
};

export type SelectGroupOption<Y = OptionValueType, T = SelectOption<Y>> = {
  options: T[];
  groupLabel: string;
};

export type SelectField<
  FormValues = null,
  T = OptionValueType
> = FormFieldBase<FormValues> & {
  type: ActionType.select;
  options: SelectOption<T>[];
};

export type SelectAutocompleteField<
  FormValues = null,
  T = OptionValueType
> = FormFieldBase<FormValues> & {
  type: ActionType.selectAutocomplete;
  options: SelectOption[];
  groupBy?: (option: SelectOption<T>) => string;
  serverSearch?: {
    fn?: (text: string) => Promise<any>;
    inputFormKey: string;
  };
  searchIcon?: boolean;
};

export type SelectGroupField<
  T = OptionValueType,
  FormValues = null
> = FormFieldBase<FormValues> & {
  type: ActionType.selectGroup;
  optionGroups: SelectGroupOption<T>[];
};

export type ButtonGroupField<
  FormValues = null,
  T = OptionValueType | boolean
> = FormFieldBase<FormValues> & {
  type: ActionType.buttonGroup;
  options: ButtonOption<T>[];
};

export type DatePickerField<FormValues = null> = FormFieldBase<FormValues> & {
  type: ActionType.datepicker;
  fullWidth?: boolean;
  multiyear?: boolean;
  shouldDisableDate?: (x: moment.Moment) => boolean;
  minDate?: Moment;
  maxDate?: Moment;
  alwaysShowCalendar?: boolean;
};

export type DateTimePickerField<FormValues = null> = FormFieldBase<FormValues> & {
  type: ActionType.datetimepicker;
  fullWidth?: boolean;
};

export type TimePickerField<FormValues = null> = FormFieldBase<FormValues> & {
  type: ActionType.timepicker;
  fullWidth?: boolean;
};

export type AutoTextField<FormValues = null> = FormFieldBase<FormValues> & {
  type: ActionType.text;
  currency?: boolean;
  percent?: boolean;
  showClearButton?: true;
  multiline?: boolean;
  minRows?: number;
  labelOnTop?: boolean;
  showCounter?: boolean;
  charLimit?: number;
  placeholder?: string;
  phoneNumber?: boolean;
  email?: boolean;
  number?: boolean;
  decimals?: number;
  allowZero?: boolean;
  allowNegative?: boolean;
  emptyValText?: string;
  optional?: boolean;
};
export type EnumField<FormValues = null> = FormFieldBase<FormValues> & {
  type: ActionType.enum;
  enum: EnumType;
};

export type BooleanField<FormValues = null, T = unknown> = FormFieldBase<FormValues> & {
  type: ActionType.boolean;
  yesValue: T;
  noValue: T;
  labelNotClickable?: boolean;
};

export type ObjectField<FormValues = null> = FormFieldBase<FormValues> & {
  type: ActionType.object;
  valueField?: string;
};

export type FormField =
  | ButtonGroupField
  | DatePickerField
  | DateTimePickerField
  | TimePickerField
  | RadioGroupField
  | SelectField
  | SelectAutocompleteField
  | SelectGroupField
  | CheckboxToggleField
  | CheckboxGroupField
  | AutoTextField
  | BooleanField
  | ObjectField
  // | DatePickerField
  | EnumField;

export interface AutoFieldProps extends InputProps {
  field: FormField;
  needsValue?: boolean;

  loading?: boolean;
  test?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  index?: number;
}

export type FieldValue = string | number | boolean;

export const getLabelOrValue = (
  values: Record<string, FieldValue>,
  field: FormField
): string | boolean => {
  if (field.type === ActionType.timepicker) {
    const val = get(values, field.id);
    if (!val) {
      return 'Not specified';
    }
  }
  if (field.type === ActionType.checkboxGroup) {
    const selectedVals = get(values, field.id, ['']);
    if (isArray(selectedVals)) {
      const pickedOptions = field.options
        .filter((o) => selectedVals.includes(o.value as string))
        .map((x) => x.label)
        .join(', ');
      return pickedOptions;
    }
  }

  if ('options' in field) {
    const pickedOption = (field.options as Option[]).find(
      (o) => o.value === get(values, field.id)
    );
    return pickedOption?.label ?? '';
  }
  const val = get(values, field.id);
  if (!val) {
    return '';
  }
  if (field.type === ActionType.text && field.currency) {
    return getCurrencyString(val as number);
  }
  if (!field.render) {
    if (typeof val === 'boolean') {
      return val;
    }
    return val.toString();
  }
  return field.render(val);
};

export function AutoField({
  field,
  needsValue = false,
  loading,
  test = '',
  disabled = false,
  error = false,
  helperText = '',
  index = -1,
  onBlur,
  ...props
}: AutoFieldProps) {
  const [fieldObj, fieldMeta, fieldHelpers] = useAutoField<FieldValue>(field.id);
  const [stepFieldObj, stepFieldMeta, stepFieldHelpers] = useAutoField<number>('step');
  const formik = useAutoForm<Record<string, FieldValue>>();
  const [_controlledValue, setControlledValue] = useState<FieldValue | undefined>(
    field?.initialValue ?? fieldObj.value
  );
  const {readOnly: readOnlyContext, emptyValText} = useContext(FormReadOnlyContext);
  const readOnlyFields: ActionType[] = [ActionType.text, ActionType.select];
  const readOnly = field.readOnly || props.readOnly || readOnlyContext;
  const controlledValue =
    field.readOnly && readOnlyFields.includes(field.type)
      ? _controlledValue || ' '
      : _controlledValue;

  useEffect(() => {
    if (field.type === ActionType.buttonGroup && field.options?.length === 1) {
      setControlledValue(field.options[0].value);
      handleFieldSubmit(field.options[0].value);
    }
  }, [field]);

  const handleFieldSubmit = useCallback(
    (value?: FieldValue) => {
      const errors = formik.errors[field.id];
      // do not update formik if there are validation errors and require submit is true
      if (errors !== undefined && field.requireSubmit) {
        return;
      }
      // only validate if not requiresubmit
      // (aka not in a step-form. Precursor does its own validation strategy due to step-wise validation)
      formik.setFieldValue(
        UPDATES._4700,
        field.id,
        value ?? controlledValue,
        !field.requireSubmit
      );
      if (field.requireSubmit && stepFieldObj.value !== undefined) {
        stepFieldHelpers.setValue(UPDATES._4701, stepFieldObj.value + 1);
      }
    },
    [formik.errors, controlledValue, field.id, field.requireSubmit]
  );

  useEffect(() => {
    setControlledValue(fieldObj.value ?? field?.initialValue);
  }, [field]);

  if (field.type === ActionType.buttonGroup) {
    const {options} = field;
    const hasError = !!fieldMeta.error && fieldMeta.touched;
    return (
      <Box className="button-group-root">
        <AutoFieldDebug>{field.id}</AutoFieldDebug>
        {field.label ? (
          <InputLabel
            error={hasError}
            style={{top: -2, fontWeight: hasError || field.boldLabel ? 600 : 'normal'}}
            htmlFor={field.id}
          >
            {field.label}
          </InputLabel>
        ) : null}
        <div
          id={field.id}
          style={{
            marginTop: '1rem',
            display: 'flex',
            columnGap: '1rem',
          }}
        >
          {options?.map((b) => (
            <Button
              key={b.label}
              variant={
                (
                  fieldObj.value !== undefined && fieldObj.value !== null
                    ? b.value === fieldObj.value
                    : b.value === field.initialValue
                )
                  ? 'contained'
                  : 'outlined'
              }
              style={{whiteSpace: 'nowrap'}}
              color="primary"
              onClick={() => {
                handleFieldSubmit(b.value);
                if (onBlur) {
                  onBlur({} as any);
                }
              }}
              disabled={b.disabled}
              size={b.size}
            >
              {b.label}
            </Button>
          ))}
        </div>
      </Box>
    );
  }
  if (field.type === ActionType.checkboxToggle) {
    return (
      <Box flex={1}>
        <AutoFieldDebug>{field.id}</AutoFieldDebug>
        <FormControl
          id={field.id}
          disabled={field.disabled || disabled}
          fullWidth={field.fullWidth}
          size={field.size || 'medium'}
          className="checkbox-root"
        >
          {/* {readOnly ? 'readonly' : 'enabled'} */}
          <Box sx={{whiteSpace: 'nowrap'}}>
            <Checkbox
              key={field.label}
              id={`${field.id}-checkbox`}
              role="checkbox"
              data-test={field.label}
              size={field.size || 'medium'}
              className="checkbox"
              onChange={() => {
                if (!readOnly && !disabled) {
                  handleFieldSubmit(!fieldObj.value);
                }
              }}
              checked={Boolean(fieldObj.value) || false}
              disabled={field.disabled || disabled}
              aria-label={field.label}
              readOnly={readOnly}
            />
            {field.boldLabel ? (
              <strong>{field.label}</strong>
            ) : (
              <span>{field.label}</span>
            )}
          </Box>
        </FormControl>
      </Box>
    );
  }
  if (field.type === ActionType.checkboxGroup) {
    const {options} = field;

    return (
      <FormControl
        id={field.id}
        disabled={field.disabled || disabled}
        fullWidth={field.fullWidth}
        size={field.size || 'small'}
        className="checkbox-group-root"
      >
        <AutoFieldDebug>{field.id}</AutoFieldDebug>
        <FormLabel disabled={field.disabled || disabled} component="legend">
          <Typography fontWeight={field.boldLabel ? 600 : 'normal'}>
            {field.label}
          </Typography>
        </FormLabel>
        <Grid
          p={0.5}
          container
          mt={0.3}
          direction={field.horizontal ? 'row' : 'column'}
          sx={{pl: field.horizontal ? 1 : 0}}
        >
          {options?.map((c) => (
            <Grid
              className="MuiCheckbox-root checkbox-root"
              xs={field.horizontalOptionWidthXs || 12}
              md={field.horizontalOptionWidthMd || field.horizontalOptionWidthXs || 12}
              lg={
                field.horizontalOptionWidthLg ||
                field.horizontalOptionWidthMd ||
                field.horizontalOptionWidthXs ||
                12
              }
              item
              key={c.label}
            >
              <FormControlLabel
                id={c.value as string}
                control={
                  <Checkbox
                    checked={
                      ((controlledValue as any as FieldValue[]) || []).includes(
                        c.value
                      ) || false
                    }
                    size={field.size}
                    sx={{p: 0.5}}
                    onChange={() => {
                      if (!readOnly && !disabled) {
                        const curr = (controlledValue as any as FieldValue[]) || [];
                        const newVal = curr.includes(c.value)
                          ? curr.filter((x) => x !== c.value)
                          : [...curr, c.value];
                        setControlledValue(newVal as any);
                        if (!field.requireSubmit) {
                          handleFieldSubmit(newVal as any);
                        }
                      }
                    }}
                    readOnly={readOnly}
                    disabled={field.disabled || disabled}
                  />
                }
                label={c.label}
              />
            </Grid>
          ))}
        </Grid>
        {field.requireSubmit && (
          <Box mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                handleFieldSubmit(controlledValue);
              }}
            >
              Confirm
            </Button>
          </Box>
        )}
      </FormControl>
    );
  }
  if (field.type === ActionType.radioGroup) {
    const {options} = field;

    return (
      <FormControl
        disabled={field.disabled || disabled}
        fullWidth={field.fullWidth}
        size={field.size || 'small'}
        className="radiogroup-root"
      >
        <AutoFieldDebug>{field.id}</AutoFieldDebug>
        <FormLabel disabled={field.disabled || disabled} component="legend">
          <Typography fontWeight={field.boldLabel ? 600 : 'normal'}>
            {field.label}
          </Typography>
        </FormLabel>
        <RadioGroup id={field.id}>
          <Grid
            p={0.5}
            container
            mt={0.3}
            direction={field.horizontal ? 'row' : 'column'}
            sx={{pl: field.horizontal ? 1 : 0}}
          >
            {options?.map((c) => (
              <Grid
                className="MuiCheckbox-root checkbox-root"
                xs={field.horizontalOptionWidthXs || 12}
                md={field.horizontalOptionWidthMd || field.horizontalOptionWidthXs || 12}
                lg={
                  field.horizontalOptionWidthLg ||
                  field.horizontalOptionWidthMd ||
                  field.horizontalOptionWidthXs ||
                  12
                }
                item
                key={c.label}
              >
                <FormControlLabel
                  value={c.value || false}
                  label={c.label}
                  control={
                    <Radio
                      sx={{p: 0.5}}
                      checked={controlledValue === c.value || false}
                      size={field.size || 'small'}
                      onChange={(e) => {
                        if (!readOnly && !disabled) {
                          let newVal: boolean | string = e.target.value;
                          if (newVal === 'true') {
                            newVal = true;
                          }
                          if (newVal === 'false') {
                            newVal = false;
                          }

                          setControlledValue(newVal as any);
                          if (!field.requireSubmit) {
                            handleFieldSubmit(newVal as any);
                          }
                        }
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                      readOnly={readOnly}
                      disabled={field.disabled}
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
        </RadioGroup>
        {field.requireSubmit && (
          <Box mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                handleFieldSubmit(controlledValue);
              }}
            >
              Confirm
            </Button>
          </Box>
        )}
      </FormControl>
    );
  }
  if (field.type === ActionType.selectAutocomplete) {
    return (
      <AutoSelectAutocomplete
        key={field.id}
        field={field}
        controlledValue={controlledValue}
        setControlledValue={setControlledValue}
        handleFieldSubmit={handleFieldSubmit}
        loading={loading}
        disabled={disabled}
        onBlur={onBlur}
        readOnly={readOnly}
        error={error}
      />
    );
  }
  if (field.type === ActionType.select || field.type === ActionType.selectGroup) {
    return (
      <AutoSelect
        field={field}
        emptyValText={emptyValText}
        controlledValue={controlledValue}
        setControlledValue={setControlledValue}
        handleFieldSubmit={handleFieldSubmit}
        loading={loading}
        disabled={disabled}
        onBlur={onBlur}
        readOnly={readOnly}
        error={error}
      />
    );
  }
  if (field.type === ActionType.text) {
    return (
      <AutoTextNew
        loading={loading || field.loading}
        field={field}
        error={error}
        emptyValText={emptyValText}
        readOnly={readOnly}
        controlledValue={controlledValue}
        setControlledValue={setControlledValue}
        disabled={disabled}
        handleFieldSubmit={handleFieldSubmit}
        onBlur={onBlur}
        {...props}
      />
    );
  }

  if (field.type === ActionType.datepicker) {
    return (
      <AutoDateTimePicker
        readOnly={readOnly}
        field={field}
        controlledValue={controlledValue as string | number}
        setControlledValue={setControlledValue}
        handleFieldSubmit={handleFieldSubmit}
        needsValue={needsValue}
        helperText={helperText}
        error={error}
        onBlur={onBlur}
        {...props}
      />
    );
  }
  if (field.type === ActionType.timepicker) {
    return (
      <AutoDateTimePicker
        readOnly={readOnly}
        field={field}
        controlledValue={controlledValue as string | number}
        setControlledValue={setControlledValue}
        handleFieldSubmit={handleFieldSubmit}
        needsValue={needsValue}
        error={error}
        onBlur={onBlur}
        {...props}
      />
    );
  }
  if (field.type === ActionType.datetimepicker) {
    return (
      <AutoDateTimePicker
        readOnly={readOnly}
        field={field}
        controlledValue={controlledValue as string | number}
        setControlledValue={setControlledValue}
        handleFieldSubmit={handleFieldSubmit}
        needsValue={needsValue}
        error={error}
        onBlur={onBlur}
        {...props}
      />
    );
  }
  if (field.type === ActionType.boolean) {
    return (
      <AutoBoolean
        defaultValue={field.noValue as any}
        field={field}
        readOnly={readOnly}
        disabled={disabled}
        controlledValue={controlledValue}
        setControlledValue={setControlledValue}
        handleFieldSubmit={handleFieldSubmit}
        error={error}
        {...props}
      />
    );
  }
  if (field.type === ActionType.object) {
    const key = field.valueField || 'id';
    return (
      <FormControl
        className={clsx({
          readOnly: field.readOnly,
          disabled: field.disabled,
          editable: !field.readOnly,
        })}
        error={!!fieldMeta.error}
        id={field.id}
        fullWidth
        size={field.size || 'small'}
        disabled={field.disabled}
      >
        <Box
          sx={{
            '& .MuiFormLabel-root.Mui-disabled': {
              color: 'inherit !important',
            },
            '&': {
              cursor: 'default',
            },
            '& input': {
              cursor: 'default',
            },
          }}
        >
          <AutoFieldDebug>{field.id}</AutoFieldDebug>
          <TextField
            id={field.id}
            fullWidth
            error={!!fieldMeta.error}
            label={field.label}
            variant="outlined"
            disabled={field.disabled}
            style={{}}
            size={field.size || 'small'}
            value={(controlledValue as any)?.[key]}
            InputLabelProps={{
              shrink: !!(controlledValue as any),
              size: 'small',
            }}
          />
          {fieldMeta.error ? (
            <Typography
              color="error"
              variant="body2"
              style={{fontSize: '0.7rem', marginTop: 4}}
            >
              {fieldMeta.error}
            </Typography>
          ) : null}
        </Box>
      </FormControl>
    );
  }
  console.error('No field given:');
  console.error(field);
  return <>No field given</>;
}

export default AutoField;

export type AutoBooleanProps = InputProps & {
  field: BooleanField;
  controlledValue: FieldValue | undefined;
  setControlledValue: React.Dispatch<React.SetStateAction<FieldValue | undefined>>;
  handleFieldSubmit: (value?: FieldValue) => void;
  readOnly: boolean;
};

export const yesNoField: ButtonGroupField = {
  id: 'yesNoField',
  label: '',
  type: ActionType.buttonGroup,
  options: [
    {
      label: 'Yes',
      value: true,
    },
    {
      label: 'No',
      value: false,
    },
  ],
};
